// @flow

import React, { Component } from 'react';
import { graphql } from '@apollo/client/react/hoc';
import { FormattedMessage } from 'react-intl';
import { FollowButton as CommonFollowButton } from '@riseart/common';
import { application as CONFIG_APP } from 'Config';
import { LocationManager } from 'shared_services/riseart/url/Location';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { FollowContext } from 'shared_data/providers/follow/Context';
import { getGraphqlOperationName, cacheUpdateHandler } from 'shared_services/apollo/helpers';
import CREATE_MUTATION from 'shared_data/queries/follow/create.graphql';
import DELETE_MUTATION from 'shared_data/queries/follow/delete.graphql';
import FILTER_FOLLOWS_QUERY from 'shared_data/queries/follow/filter.graphql';

type OwnProps = {
  leaderId: number,
  leaderData: Object,
  forwardUrl?: string,
  displayType?: string,
};

type Props = {
  ...OwnProps,
  actionCreateFollow: Function,
  actionDeleteFollow: Function,
  isAllowed: boolean,
  follow: Function,
  unfollow: Function,
  loaded: boolean,
  error: ?boolean,
  items: Array<Object>,
  followerId: number,
  loadingFollowList: boolean,
  hideText?: boolean,
};

type State = {
  isFollowed: ?boolean,
  isLoading: boolean,
};

const GQL_NAMES = {
  create: getGraphqlOperationName(CREATE_MUTATION),
  delete: getGraphqlOperationName(DELETE_MUTATION),
  filter: getGraphqlOperationName(FILTER_FOLLOWS_QUERY),
};

/**
 * FollowButtonComponent
 */
class FollowButtonComponent extends Component<Props, State> {
  followInfo: ?Object = null;

  /**
   * constructor
   *
   * @param {Props} props
   */
  constructor(props: Props) {
    super(props);

    this.state = {
      isFollowed: !!this.getFollowItem(),
      isLoading: !!this.props.loadingFollowList,
    };
    this.bindMethods();
  }

  /**
   * componentDidUpdate
   *
   * @param {Props} prevProps
   */
  componentDidUpdate(prevProps: Props) {
    let updatedState = null;

    // Update loading state if props are different
    if (this.props.loadingFollowList !== prevProps.loadingFollowList) {
      updatedState = {
        ...(updatedState || {}),
        isLoading: this.props.loadingFollowList,
      };
    }

    // Update isFollowed state if prop leaderId id different or if follow list loaded
    if (
      this.props.leaderId !== prevProps.leaderId ||
      this.props.loaded !== prevProps.loaded ||
      this.getFollowItem(this.props) !== this.getFollowItem(prevProps)
    ) {
      updatedState = {
        ...(updatedState || {}),
        isFollowed: !!this.getFollowItem(),
      };
    }

    if (updatedState) {
      this.setState(updatedState);
    }
  }

  /**
   * handleClick
   */
  handleClick: Function;

  handleClick() {
    if (this.state.isLoading) {
      return;
    }

    const { isFollowed: wasFollowed } = this.state;
    const followInfo = this.getFollowItem();

    // Optimistic update
    this.setState({ isLoading: true, isFollowed: !wasFollowed });

    if (wasFollowed && followInfo) {
      // Was followed, then unfollow
      this.props
        .unfollow(followInfo.id)
        .then(() => {
          this.props.actionDeleteFollow(this.collectGTMData());
          this.setState({ isLoading: false });
        })
        .catch(() => {
          this.setState({ isLoading: false, isFollowed: true });
        });
    } else {
      // Was not followed, then follow
      this.props
        .follow()
        .then(() => {
          this.props.actionCreateFollow(this.collectGTMData());
          this.setState({ isLoading: false });
        })
        .catch(() => {
          this.setState({ isLoading: false, isFollowed: false });
        });
    }
  }

  /**
   * getFollowItem
   *
   * @returns {?Object} found item or undefined
   */
  getFollowItem(data?: Object): ?Object {
    const { leaderId, items } = data || this.props;

    return items[leaderId];
  }

  /**
   * collectGTMData
   *
   * @returns {Object}
   */
  collectGTMData(): Object {
    return { ...this.props.leaderData, id: this.props.leaderId };
  }

  /**
   * bindMethods
   */
  bindMethods() {
    this.handleClick = this.handleClick.bind(this);
  }

  /**
   * render
   */
  render() {
    const { isFollowed } = this.state;

    if (this.props.error) {
      return null;
    }

    if (this.props.isAllowed) {
      return (
        <CommonFollowButton
          displayType={this.props.displayType}
          isFollowed={isFollowed}
          onClick={this.handleClick}
        >
          {this.props.hideText ? null : (
            <FormattedMessage id={isFollowed ? 'common.unfollow' : 'common.follow'}>
              {(follow: string) => follow}
            </FormattedMessage>
          )}
        </CommonFollowButton>
      );
    }

    return (
      <CommonFollowButton
        href={UrlAssembler.byRouteKey('register', {
          search: {
            role: CONFIG_APP.acl.resourcePermissions.follow,
            forward: this.props.forwardUrl || LocationManager.get('uri'),
          },
          hash: { follow: this.props.leaderId },
        })}
      >
        {this.props.hideText ? null : (
          <FormattedMessage id="common.follow">{(follow: string) => follow}</FormattedMessage>
        )}
      </CommonFollowButton>
    );
  }
}

/**
 * withCreateFollow
 */
// $FlowFixMe
export const withCreateFollow = graphql(CREATE_MUTATION, {
  props: ({ ownProps, mutate }: Object) => ({
    follow(leaderId) {
      const variables = {
        followerId: ownProps.followerId,
        leaderId: leaderId || ownProps.leaderId,
      };
      return mutate({
        variables,
        update: cacheUpdateHandler(
          FILTER_FOLLOWS_QUERY,
          GQL_NAMES.create,
          variables,
          (cache, updatedData) => {
            const currentData = cache.readQuery({ query: FILTER_FOLLOWS_QUERY, variables });

            if (
              !updatedData ||
              !currentData ||
              !currentData.filterFollows ||
              !currentData.filterFollows.items
            ) {
              return;
            }

            let newItems = [];

            if (currentData.filterFollows.items.some((item) => item.id === updatedData.id)) {
              newItems = currentData.filterFollows.items.map((item) =>
                item.id === updatedData.id ? updatedData : item,
              );
            } else {
              newItems = [...currentData.filterFollows.items, updatedData];
            }

            // Add new follow record to query cache
            cache.writeQuery({
              query: FILTER_FOLLOWS_QUERY,
              variables: { followerId: ownProps.followerId },
              data: {
                [GQL_NAMES.filter]: {
                  ...currentData.filterFollows,
                  items: newItems,
                },
              },
            });
          },
        ),
      });
    },
  }),
});

/**
 * withDeleteFollow
 */
// $FlowFixMe
const withDeleteFollow = graphql(DELETE_MUTATION, {
  props: ({ ownProps, mutate }: Object) => ({
    unfollow(id) {
      const variables = { id };

      return mutate({
        variables: { id },
        update: cacheUpdateHandler(
          FILTER_FOLLOWS_QUERY,
          GQL_NAMES.delete,
          variables,
          (cache, updatedData) => {
            const currentData = cache.readQuery({
              query: FILTER_FOLLOWS_QUERY,
              variables: {
                followerId: ownProps.followerId,
                leaderId: ownProps.leaderId,
              },
            });

            if (
              !updatedData ||
              !updatedData.id ||
              !currentData ||
              !currentData.filterFollows ||
              !currentData.filterFollows.items
            ) {
              return;
            }

            // Remove unfollowed record from cache
            cache.writeQuery({
              query: FILTER_FOLLOWS_QUERY,
              variables: { followerId: ownProps.followerId },
              data: {
                [GQL_NAMES.filter]: {
                  ...currentData.filterFollows,
                  items: currentData.filterFollows.items.filter(
                    (item) => item.id !== updatedData.id,
                  ),
                },
              },
            });
          },
        ),
      });
    },
  }),
});

const FollowButtonWithMutations = withCreateFollow(withDeleteFollow(FollowButtonComponent));

/**
 * FollowButton
 *
 * @param {OwnProps} ownProps
 */
export const FollowButton = (ownProps: OwnProps) => (
  <FollowContext.Consumer>
    {(followProps) => <FollowButtonWithMutations {...ownProps} {...followProps} />}
  </FollowContext.Consumer>
);
